import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
/**
 * ShareOfWallet — who actually owns this account's money.
 *
 * Replaces `topVendors.slice(0,6).map(...).join('  ·  ')`: six dollar figures
 * welded into one unclickable sentence, in a panel with room for a picture.
 * Area is spend, so the answer arrives before any digit is read, and OUR tile
 * is the only saturated one on the board — "how big are we here" is the whole
 * question the panel exists to answer.
 *
 * Presentational: data via props, no fetching, colours from CSS variables.
 */
import { useMemo } from 'react';
import { ResponsiveContainer, Treemap, Tooltip } from 'recharts';
import { ChartDrillList } from './ChartDrillList.js';
const defaultFormatValue = (v) => {
    if (v >= 1000000000)
        return `$${(v / 1000000000).toFixed(1)}B`;
    if (v >= 1000000)
        return `$${(v / 1000000).toFixed(1)}M`;
    if (v >= 1000)
        return `$${(v / 1000).toFixed(0)}K`;
    return `$${Math.round(v)}`;
};
function WalletTooltip({ active, payload, total, formatValue, }) {
    if (!active || !payload || payload.length === 0)
        return null;
    const d = payload[0].payload;
    if (!d || !d.name)
        return null;
    const pct = total > 0 ? ((d.value / total) * 100).toFixed(1) : null;
    return (_jsxs("div", { className: "cht cht--compact", children: [_jsx("strong", { className: "cht__strong", children: d.name }), _jsxs("div", { className: "cht__figure", children: [d.valueFormatted || formatValue(d.value), pct && (_jsxs("span", { className: "cht__sub", children: [' ', "\u00B7 ", pct, "%"] }))] })] }));
}
/** Treemap content: recharts hands us the rect, we paint the tile. */
function Tile(props) {
    const x = props.x;
    const y = props.y;
    const width = props.width;
    const height = props.height;
    const slice = props;
    const depth = props.depth ?? 1;
    if (!(width > 0) || !(height > 0))
        return null;
    // recharts paints the synthetic ROOT node through `content` as well as the
    // leaves. Drawing it lays a full-size tile under the real ones — a grey wash
    // that dulls every colour above it and, worse, is itself clickable. Only
    // leaves are tiles.
    if (depth === 0 || !slice.name)
        return null;
    const ours = !!slice.ours;
    const residual = !!slice.residual;
    // Everything that is not ours recedes, and recedes further down the ranking,
    // so one saturated tile carries the whole read.
    const fillColor = ours
        ? 'var(--accent, #3b82f6)'
        : residual
            ? 'var(--color-text-muted, var(--text-muted, #9ca3af))'
            : 'var(--color-text-muted, var(--text-muted, #9ca3af))';
    // `tone` is computed with the data, not read off the node: recharts' node
    // props carry no rank, so keying the fade off one silently gave every
    // competitor tile the same weight.
    const fillOpacity = ours ? 1 : residual ? 0.16 : (slice.tone ?? 0.4);
    // Only label a tile big enough to hold the label without clipping it.
    const roomForName = width > 62 && height > 26;
    const roomForValue = width > 76 && height > 42;
    const labelColor = ours ? '#fff' : 'var(--color-text, var(--text, #111827))';
    return (_jsxs("g", { children: [_jsx("rect", { x: x, y: y, width: width, height: height, fill: fillColor, fillOpacity: fillOpacity, stroke: "var(--color-surface, var(--surface, #fff))", strokeWidth: 2 }), roomForName && (_jsx("text", { x: x + 8, y: y + 19, fill: labelColor, fontSize: 12, fontWeight: 600, className: "chlabel", children: slice.name })), roomForValue && (_jsx("text", { x: x + 8, y: y + 35, fill: labelColor, fontSize: 11, fillOpacity: ours ? 0.9 : 0.75, className: "chlabel", children: slice.valueFormatted || defaultFormatValue(slice.value) }))] }));
}
export const ShareOfWallet = ({ vendors, totalSpend, ourVendorMatch = 'palo alto', topN = 8, residualLabel, height = 260, fill = false, onPick, formatValue = defaultFormatValue, }) => {
    const { data, total } = useMemo(() => {
        /*
         * THE FIELD NAMES HAD DRIFTED, AND THE CHART SHOWED ONE GREY BOX.
         *
         * Ryan, looking at CDT: "What in the world is the value of this view?"
         * Fair — it rendered a single rectangle reading "Everyone else · $4.1B"
         * and nothing else. Not a design failure. A read failure:
         *
         *   /api/v1/procurement/summary returns  { vendor, spend, spendFormatted }
         *   this read                            v.total, v.totalFormatted
         *
         * `Number(undefined) || 0` is 0, so every vendor scored zero, the
         * `value > 0` filter below dropped all ten of them, `namedSum` came out 0,
         * and the residual swallowed the entire wallet. The widget was drawing the
         * whole account as "everyone else" — which is exactly as useless as it
         * looked, and silent, because a zero is a perfectly good number.
         *
         * Verified against the live payload: SHI $1.08B, Crayon $508M, American
         * Dark Fiber $423M, Rackspace $224M, Dell $176M — $3.03B across ten
         * vendors that the chart was rendering as nothing.
         *
         * Both spellings are accepted rather than picking one: this component is
         * shared, and a caller that already passes `total` must keep working.
         */
        const rows = (vendors || [])
            .map((v) => ({
            name: (v.vendor || v.name || '').trim(),
            value: Math.max(Number(v.total ?? v.spend ?? v.value) || 0, 0),
            valueFormatted: v.totalFormatted ?? v.spendFormatted ?? null,
        }))
            .filter((v) => v.name && v.value > 0)
            .sort((a, b) => b.value - a.value);
        const top = rows.slice(0, topN);
        const named = top.map((v, i) => ({
            ...v,
            ours: v.name.toLowerCase().includes(ourVendorMatch),
            // Competitors recede down the ranking so the eye lands on the biggest
            // few and on ours, rather than on eight tiles of identical weight.
            tone: Math.max(0.5 - i * 0.05, 0.22),
        }));
        const namedSum = named.reduce((s, v) => s + v.value, 0);
        const grand = Number(totalSpend) > 0 ? Number(totalSpend) : rows.reduce((s, v) => s + v.value, 0);
        // The remainder is a real tile, not a footnote: without it the biggest
        // named vendor looks like the whole account.
        const rest = Math.max(grand - namedSum, 0);
        const restCount = Math.max(rows.length - named.length, 0);
        const slices = [...named];
        if (rest > 0 && (restCount > 0 || rest / grand > 0.02)) {
            slices.push({
                name: residualLabel || (restCount > 0 ? `${restCount} others` : 'Everyone else'),
                value: rest,
                residual: true,
            });
        }
        return { data: slices, total: grand };
    }, [vendors, totalSpend, ourVendorMatch, topN, residualLabel]);
    if (data.length === 0) {
        return (_jsx("div", { className: `chs__empty${fill ? ' chs__empty--fill' : ''}`, style: fill ? undefined : { '--h': `${height}px` }, children: "No vendor spend recorded." }));
    }
    return (_jsxs(_Fragment, { children: [_jsx(ResponsiveContainer, { width: "100%", height: fill ? '100%' : height, className: fill ? 'ts-chart-fill' : undefined, children: _jsx(Treemap, { data: data, dataKey: "value", 
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    content: _jsx(Tile, {}), isAnimationActive: false, onClick: onPick ? (node) => {
                        const d = node;
                        // The residual is an aggregate; there is no single vendor behind it
                        // to open, so it stays inert rather than opening the wrong thing.
                        if (d && d.name && !d.residual)
                            onPick(d);
                    } : undefined, className: onPick ? 'chpick' : undefined, children: _jsx(Tooltip, { content: _jsx(WalletTooltip, { total: total, formatValue: formatValue }) }) }) }), _jsx(ChartDrillList, { label: "Share of wallet \u2014 open transactions by vendor", drills: onPick ? data.filter((d) => !d.residual && d.value > 0).map((d) => ({
                    key: d.name,
                    label: `${d.name}, ${d.valueFormatted || formatValue(d.value)} — open transactions`,
                    onSelect: () => onPick(d),
                })) : [] })] }));
};
export default ShareOfWallet;
